import React from 'react';
import {
  Avatar,
  Box,
  Breadcrumbs,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  Link,
  ListItemIcon,
  Menu,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import NavigateNextRoundedIcon from '@mui/icons-material/NavigateNextRounded';
import MenuIcon from '@mui/icons-material/Menu';
import Logout from '@mui/icons-material/Logout';
import People from '@mui/icons-material/People';
import AccountCircle from '@mui/icons-material/AccountCircle';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import InstallDesktopRoundedIcon from '@mui/icons-material/InstallDesktopRounded';
import { useUnit } from 'effector-react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';

import { $isSettlementTenant, $user, logoutFx } from '../store';
import { resolveUserRole, userHasRole } from '../config/roles';
import {
  $activeTenantAccessLevel,
  $activeTenantId,
  $adminTenants,
  $contractorTenants,
  fetchAdminTenantsFx,
  setActiveTenantId,
} from '../store/tenantContext';
import { usePwa } from '../pwa/PwaProvider';
import { disablePushForCurrentBrowser } from '../pwa/webPushClient';
import { getObjectKindLabels } from '../utils/complexKind';
import WebNotificationsButton from './WebNotificationsButton';

function initialsFromUser(user) {
  const name = user?.name || user?.fullName || user?.email || '';
  const parts = String(name).trim().split(/\s+/);
  const letters = parts.length >= 2 ? `${parts[0][0]}${parts[1][0]}` : parts[0]?.[0] || '?';
  return letters.toUpperCase();
}

function getSectionTitles(labels) {
  return [
    { match: (pathname) => pathname === '/home', title: 'Главная панель' },
    { match: (pathname) => pathname.startsWith('/gateways'), title: 'Шлюзы и сеть' },
    { match: (pathname) => pathname.startsWith('/edge-agents'), title: 'Агенты' },
    { match: (pathname) => pathname.startsWith('/lorawan/diagnostics'), title: 'Диагностика LoRaWAN' },
    { match: (pathname) => pathname.startsWith('/calculations'), title: 'Расчёты' },
    { match: (pathname) => pathname.startsWith('/premises'), title: labels.premises },
    { match: (pathname) => pathname.startsWith('/settlement/electricity'), title: 'Электроэнергия' },
    { match: (pathname) => pathname.startsWith('/tickets/'), title: 'Карточка заявки' },
    { match: (pathname) => pathname.startsWith('/tickets'), title: 'Заявки' },
    { match: (pathname) => pathname.startsWith('/complex/'), title: labels.complex },
    {
      match: (pathname) => pathname.startsWith('/building/') && pathname.endsWith('/corps'),
      title: labels.house,
    },
    { match: (pathname) => pathname.startsWith('/building/') && pathname.endsWith('/manual-progress'), title: 'Прогресс показаний' },
    { match: (pathname) => pathname.startsWith('/corps/'), title: 'Устройства' },
    { match: (pathname) => pathname.startsWith('/meter-readings/progress'), title: 'Прогресс сбора показаний' },
    { match: (pathname) => pathname.startsWith('/meter-readings/imports'), title: 'Импорт показаний' },
    { match: (pathname) => pathname.startsWith('/admin/add-client'), title: 'Добавление клиента' },
    { match: (pathname) => pathname.startsWith('/admin/contractors'), title: 'Подрядчики' },
    { match: (pathname) => pathname.startsWith('/admin/field-staff'), title: 'Полевые сотрудники' },
    { match: (pathname) => pathname.startsWith('/admin/add-complex'), title: labels.addComplex },
    { match: (pathname) => pathname.startsWith('/admin/bulk-import'), title: 'Массовый импорт' },
    { match: (pathname) => pathname.startsWith('/admin/onboarding'), title: 'Быстрый старт' },
    { match: (pathname) => pathname.startsWith('/admin/integrations'), title: 'API-интеграции' },
    { match: (pathname) => pathname.startsWith('/admin/lorawan'), title: 'BillingService LoRaWAN' },
    { match: (pathname) => pathname.startsWith('/admin/mobile-news'), title: 'Новости мобильного приложения' },
  ];
}

function buildBreadcrumbs(pathname, title, labels) {
  if (!pathname || pathname === '/home') {
    return [];
  }

  const complexRoot = labels.complexPlural;

  const routes = [
    {
      match: (value) => value.startsWith('/gateways'),
      crumbs: [{ label: 'Шлюзы', href: '/gateways' }],
    },
    {
      match: (value) => value.startsWith('/lorawan/diagnostics'),
      crumbs: [{ label: 'Шлюзы и сеть', href: '/gateways' }, { label: title }],
    },
    {
      match: (value) => value.startsWith('/calculations'),
      crumbs: [{ label: 'Расчёты', href: '/calculations' }],
    },
    {
      match: (value) => value.startsWith('/premises'),
      crumbs: [{ label: labels.premises, href: '/premises' }],
    },
    {
      match: (value) => value.startsWith('/tickets/'),
      crumbs: [{ label: 'Заявки', href: '/tickets' }, { label: 'Карточка заявки' }],
    },
    {
      match: (value) => value.startsWith('/tickets'),
      crumbs: [{ label: 'Заявки', href: '/tickets' }],
    },
    {
      match: (value) => value.startsWith('/complex/'),
      crumbs: [{ label: complexRoot, href: '/home' }, { label: title }],
    },
    {
      match: (value) => value.startsWith('/building/') && value.endsWith('/corps'),
      crumbs: [{ label: complexRoot, href: '/home' }, { label: title }],
    },
    {
      match: (value) => value.startsWith('/building/') && value.endsWith('/manual-progress'),
      crumbs: [{ label: complexRoot, href: '/home' }, { label: title }],
    },
    {
      match: (value) => value.startsWith('/corps/'),
      crumbs: [{ label: complexRoot, href: '/home' }, { label: title }],
    },
    {
      match: (value) => value.startsWith('/meter-readings/progress'),
      crumbs: [{ label: 'Показания', href: '/meter-readings/progress' }, { label: title }],
    },
    {
      match: (value) => value.startsWith('/meter-readings/imports'),
      crumbs: [{ label: 'Импорт показаний', href: '/meter-readings/imports' }, { label: title }],
    },
    {
      match: (value) => value.startsWith('/admin/add-client'),
      crumbs: [{ label: 'Администрирование', href: '/home' }, { label: title }],
    },
    {
      match: (value) => value.startsWith('/admin/contractors'),
      crumbs: [{ label: 'Администрирование', href: '/home' }, { label: title }],
    },
    {
      match: (value) => value.startsWith('/admin/field-staff'),
      crumbs: [{ label: 'Администрирование', href: '/home' }, { label: title }],
    },
    {
      match: (value) => value.startsWith('/admin/add-complex'),
      crumbs: [{ label: 'Администрирование', href: '/home' }, { label: title }],
    },
    {
      match: (value) => value.startsWith('/admin/bulk-import'),
      crumbs: [{ label: 'Администрирование', href: '/home' }, { label: title }],
    },
    {
      match: (value) => value.startsWith('/admin/onboarding'),
      crumbs: [{ label: 'Администрирование', href: '/home' }, { label: title }],
    },
    {
      match: (value) => value.startsWith('/admin/integrations'),
      crumbs: [{ label: 'Администрирование', href: '/home' }, { label: title }],
    },
    {
      match: (value) => value.startsWith('/admin/lorawan'),
      crumbs: [{ label: 'Администрирование', href: '/home' }, { label: title }],
    },
    {
      match: (value) => value.startsWith('/admin/mobile-news'),
      crumbs: [{ label: 'Администрирование', href: '/home' }, { label: title }],
    },
  ];

  const matched = routes.find((route) => route.match(pathname));
  return matched ? matched.crumbs : [{ label: title }];
}

function getTenantSwitchTarget(pathname) {
  if (/^\/tickets\/[^/]+\/?$/.test(pathname)) return '/tickets';
  if (/^\/apartments\/[^/]+\/?$/.test(pathname)) return '/premises';
  if (/^\/(complex|corps)\/[^/]+\/?$/.test(pathname)) return '/home';
  if (/^\/building\/[^/]+\//.test(pathname)) return '/home';
  return null;
}

export default function AppHeader({ title = '', onGoToUsers }) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [showSettings, setShowSettings] = React.useState(false);
  const open = Boolean(anchorEl);

  const user = useUnit($user);
  const isSettlementTenant = useUnit($isSettlementTenant);
  const contractorTenants = useUnit($contractorTenants);
  const adminTenants = useUnit($adminTenants);
  const adminTenantsLoading = useUnit(fetchAdminTenantsFx.pending);
  const activeTenantId = useUnit($activeTenantId);
  const activeAccessLevel = useUnit($activeTenantAccessLevel);
  const updateActiveTenant = useUnit(setActiveTenantId);
  const { canInstall, installApp } = usePwa();

  const location = useLocation();
  const navigate = useNavigate();

  const displayRole = resolveUserRole(user, '');
  const isSuperadmin = userHasRole(user, 'superadmin');
  const isContractor = userHasRole(user, 'contractor');
  const isHomePage = location.pathname === '/home';
  const labels = React.useMemo(
    () => getObjectKindLabels(isSettlementTenant),
    [isSettlementTenant]
  );
  const sectionTitles = React.useMemo(() => getSectionTitles(labels), [labels]);
  const resolvedTitle =
    title ||
    sectionTitles.find((item) => item.match(location.pathname))?.title ||
    'Раздел';
  const breadcrumbs = React.useMemo(
    () => buildBreadcrumbs(location.pathname, resolvedTitle, labels),
    [location.pathname, resolvedTitle, labels]
  );

  const widgetDefaults = {
    indicators: true,
    network: true,
    quickActions: true,
    quality: true,
  };

  const [widgetSettings, setWidgetSettings] = React.useState(() => {
    try {
      const raw = localStorage.getItem('home.widgets');
      return raw ? { ...widgetDefaults, ...JSON.parse(raw) } : widgetDefaults;
    } catch {
      return widgetDefaults;
    }
  });

  const updateWidgetSettings = (next) => {
    setWidgetSettings(next);
    try {
      localStorage.setItem('home.widgets', JSON.stringify(next));
    } catch {}
    window.dispatchEvent(new Event('home-widgets-changed'));
  };

  const handleLogout = async () => {
    try {
      await disablePushForCurrentBrowser().catch(() => {});
      await logoutFx();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleTenantChange = (event) => {
    const nextTenantId = String(event.target.value);
    if (!nextTenantId || nextTenantId === activeTenantId) return;

    const target = getTenantSwitchTarget(location.pathname);
    updateActiveTenant(nextTenantId);
    if (target) navigate(target, { replace: true });
  };

  return (
    <Box
      component="header"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 15,
        px: { xs: 2, sm: 3 },
        pt: { xs: 1.25, sm: 1.5 },
        pb: 1,
        mb: isHomePage ? 0.75 : 2,
        backdropFilter: 'blur(14px)',
        background:
          'linear-gradient(180deg, color-mix(in oklab, var(--app-bg) 86%, transparent), color-mix(in oklab, var(--app-bg) 72%, transparent))',
        borderBottom: '1px solid color-mix(in oklab, var(--border-color) 82%, transparent)',
      }}
    >
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        spacing={1.5}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', lg: 'center' }}
      >
        <Stack spacing={0.35} sx={{ minWidth: 0 }}>
          {!isHomePage && breadcrumbs.length > 0 && (
            <Breadcrumbs
              separator={<NavigateNextRoundedIcon sx={{ fontSize: 14 }} />}
              aria-label="breadcrumb"
              sx={{
                '& .MuiBreadcrumbs-ol': {
                  flexWrap: 'nowrap',
                },
              }}
            >
              <Link
                component={RouterLink}
                to="/home"
                underline="hover"
                color="inherit"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  fontSize: 12,
                  color: 'text.secondary',
                }}
              >
                <HomeRoundedIcon sx={{ fontSize: 14 }} />
                Главная
              </Link>
              {breadcrumbs.map((crumb, index) =>
                crumb.isLast || index === breadcrumbs.length - 1 ? (
                  <Typography
                    key={crumb.href || `${crumb.label}-${index}`}
                    sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 600 }}
                  >
                    {crumb.label}
                  </Typography>
                ) : (
                  <Link
                    key={crumb.href}
                    component={RouterLink}
                    to={crumb.href}
                    underline="hover"
                    color="inherit"
                    sx={{ fontSize: 12, color: 'text.secondary' }}
                  >
                    {crumb.label}
                  </Link>
                )
              )}
            </Breadcrumbs>
          )}

          {resolvedTitle ? (
            <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.05, letterSpacing: '-0.02em' }}>
              {resolvedTitle}
            </Typography>
          ) : null}
        </Stack>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            width: { xs: '100%', lg: 'auto' },
            justifyContent: { xs: 'space-between', lg: 'flex-end' },
          }}
        >
          {(isContractor || isSuperadmin) && (
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel id="active-tenant-label">Клиент</InputLabel>
                <Select
                  labelId="active-tenant-label"
                  value={activeTenantId || ''}
                  label="Клиент"
                  onChange={handleTenantChange}
                >
                  {((isContractor && contractorTenants.length === 0) ||
                    (isSuperadmin && adminTenants.length === 0)) && (
                    <MenuItem value="" disabled>
                      {adminTenantsLoading ? 'Загрузка...' : 'Нет доступных клиентов'}
                    </MenuItem>
                  )}
                  {isContractor &&
                    contractorTenants.map((tenant) => (
                      <MenuItem key={tenant.tenant_id} value={tenant.tenant_id}>
                        {tenant.tenant_name || tenant.tenant_schema || tenant.tenant_id}
                      </MenuItem>
                    ))}
                  {isSuperadmin &&
                    adminTenants.map((tenant) => (
                      <MenuItem key={tenant.id} value={tenant.id}>
                        {tenant.name || tenant.schema_name || tenant.id}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>

              {isContractor && activeAccessLevel && (
                <Chip
                  size="small"
                  label={activeAccessLevel === 'write' ? 'Редактирование' : 'Только чтение'}
                  color={activeAccessLevel === 'write' ? 'success' : 'default'}
                  variant="outlined"
                />
              )}
            </Stack>
          )}

          <WebNotificationsButton tenantId={activeTenantId || user?.tenant_id} />

          <Tooltip title={user?.email || 'Профиль'}>
            <Avatar sx={{ width: 36, height: 36 }}>{initialsFromUser(user)}</Avatar>
          </Tooltip>

          <IconButton
            size="large"
            onClick={(e) => setAnchorEl(e.currentTarget)}
            aria-controls={open ? 'app-header-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            sx={{
              border: '1px solid var(--border-color)',
              background: 'var(--surface-bg)',
            }}
          >
            <MenuIcon />
          </IconButton>

          <Menu
            id="app-header-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={() => setAnchorEl(null)}
            PaperProps={{
              elevation: 0,
              sx: {
                width: 357,
                minHeight: 219,
                borderRadius: '20px',
                border: '1px solid var(--border-color)',
                overflow: 'hidden',
                mt: 1,
                backgroundColor: (t) => t.palette.background.paper,
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem disabled sx={{ opacity: 1, py: 1.5 }}>
              <ListItemIcon>
                <AccountCircle fontSize="small" />
              </ListItemIcon>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="body1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                  {displayRole}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {user?.email || 'email не указан'}
                </Typography>
              </Box>
            </MenuItem>

            <Divider />

            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                onGoToUsers?.();
              }}
            >
              <ListItemIcon>
                <People fontSize="small" />
              </ListItemIcon>
              Список пользователей
            </MenuItem>

            <Divider />

            <MenuItem onClick={() => setShowSettings((prev) => !prev)}>
              <ListItemIcon>
                <MenuIcon fontSize="small" />
              </ListItemIcon>
              Настройки виджетов
            </MenuItem>

            {showSettings && (
              <Box sx={{ px: 2, pb: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Главная страница
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={widgetSettings.indicators}
                      onChange={(e) =>
                        updateWidgetSettings({ ...widgetSettings, indicators: e.target.checked })
                      }
                    />
                  }
                  label="Показатели"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={widgetSettings.network}
                      onChange={(e) =>
                        updateWidgetSettings({ ...widgetSettings, network: e.target.checked })
                      }
                    />
                  }
                  label="Состояние сети"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={widgetSettings.quality}
                      onChange={(e) =>
                        updateWidgetSettings({ ...widgetSettings, quality: e.target.checked })
                      }
                    />
                  }
                  label="Качество данных"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={widgetSettings.quickActions}
                      onChange={(e) =>
                        updateWidgetSettings({ ...widgetSettings, quickActions: e.target.checked })
                      }
                    />
                  }
                  label="Быстрые действия"
                />
              </Box>
            )}

            {canInstall ? (
              [
                <Divider key="install-divider" />,
                <MenuItem
                  key="install-app"
                  onClick={() => {
                    setAnchorEl(null);
                    installApp();
                  }}
                >
                  <ListItemIcon>
                    <InstallDesktopRoundedIcon fontSize="small" />
                  </ListItemIcon>
                  Установить приложение
                </MenuItem>,
              ]
            ) : null}

            <Divider />

            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                handleLogout();
              }}
            >
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              Выйти
            </MenuItem>
          </Menu>
        </Box>
      </Stack>
    </Box>
  );
}
